import { createFileRoute, Link } from "@tanstack/react-router";
import { Target, MousePointerClick, DollarSign, Layers, BarChart3, Sparkles, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/marketing/paid-advertising/")({
  component: PaidAdsIndex,
  head: () => ({
    meta: [
      { title: "Paid Advertising Services — CoderLity" },
      { name: "description", content: "Performance ads across Google, Meta, TikTok, LinkedIn and programmatic — built to scale ROAS." },
      { property: "og:title", content: "Paid Advertising Services — CoderLity" },
      { property: "og:description", content: "Performance ad management across every major channel with creative, tracking and constant optimization." },
      { property: "og:url", content: "/marketing/paid-advertising" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/paid-advertising" }],
  }),
});

const channels = [
  { slug: "google-ads", title: "Google Ads", desc: "Explore google ads" },
  { slug: "meta-ads", title: "Meta Ads", desc: "Explore meta ads" },
  { slug: "linkedin-ads", title: "LinkedIn Ads", desc: "Explore linkedin ads" },
  { slug: "tiktok-ads", title: "TikTok Ads", desc: "Explore tiktok ads" },
];

const optimization = [
  { slug: "landing-pages", title: "Landing Pages", desc: "Explore landing pages" },
  { slug: "ab-testing", title: "A/B Testing", desc: "Explore a/b testing" },
  { slug: "tracking-pixels", title: "Tracking & Pixels", desc: "Explore tracking & pixels" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/marketing/paid-advertising/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function PaidAdsIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Channels" items={channels} />
        <Group title="Optimization" items={optimization} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Marketing",
  title: "Paid Advertising",
  sub: "Performance ads across every major channel.",
  intro:
    "We treat paid media like a P&L, not a vanity metric. Every campaign is built around CAC and ROAS targets, with creative testing, landing-page optimization and server-side tracking baked in. From $5k/month startups to $1M/month enterprise spend, our paid team runs Google, Meta, TikTok, LinkedIn and programmatic as one connected engine.",
  features: [
    { icon: Target, title: "Channel strategy", description: "The right channel mix for your buyer journey and CAC targets." },
    { icon: Sparkles, title: "Creative production", description: "Static, video and UGC ad creatives produced in-house weekly." },
    { icon: MousePointerClick, title: "Landing page CRO", description: "Dedicated landers per campaign with split-tested elements." },
    { icon: Layers, title: "Server-side tracking", description: "GTM server containers and Conversions API for accurate attribution." },
    { icon: DollarSign, title: "Budget pacing", description: "Daily pacing reviews and reallocation across campaigns." },
    { icon: BarChart3, title: "MMM + attribution", description: "Triangulate platform data with media-mix modeling for truth." },
  ],
  process: [
    { step: "01", title: "Audit & forecast", description: "Account audit, benchmarks and 12-month CAC/ROAS forecast." },
    { step: "02", title: "Build", description: "Campaign structure, creative production, tracking setup." },
    { step: "03", title: "Launch", description: "Phased rollout with daily monitoring and bid adjustments." },
    { step: "04", title: "Scale", description: "Weekly creative refresh, audience expansion and budget scaling." },
  ],
  deliverables: [
    "Full ad account audit",
    "Campaign builds across selected channels",
    "Creative production (10–30 assets / month)",
    "Server-side tracking and Conversions API",
    "Weekly performance reports + monthly strategy reviews",
    "Dedicated account manager and media buyer",
  ],
  techStack: ["Google Ads", "Meta Ads", "TikTok Ads", "LinkedIn Ads", "Reddit Ads", "GA4", "GTM Server", "Northbeam", "Triple Whale"],
  pricing: [
    { name: "Single channel", price: "$2.5k", period: "month", features: ["1 platform", "Up to $20k ad spend", "5 creatives / month", "Weekly reports"] },
    { name: "Multi-channel", price: "$6k", period: "month", highlighted: true, features: ["3 platforms", "Up to $100k ad spend", "15 creatives / month", "Bi-weekly calls"] },
    { name: "Performance", price: "10% of spend", features: ["All channels", "$100k+ ad spend", "30+ creatives / month", "Dedicated team"] },
  ],
  faqs: [
    { q: "Do you require a minimum ad spend?", a: "We work best with brands spending $10k+/month on ads. Below that, paid often isn't the right channel yet." },
    { q: "Who makes the creative?", a: "Our in-house designers and video editors. We produce static, motion and UGC weekly so your ads never go stale." },
    { q: "Do you handle landing pages too?", a: "Yes — every campaign gets a dedicated landing page that we A/B test against the control." },
    { q: "How is attribution handled?", a: "We deploy GTM server containers and Conversions API for platform-level accuracy, then layer in MMM for cross-channel truth." },
  ],
};
